/**
 * Plain-language vocabulary for life events — WP-PLAN-1 Phase 3.
 *
 * WHY THIS EXISTS
 * ---------------
 * The stored model has two orthogonal fields:
 *
 *   eventType   'lump_sum' | 'cash_flow'      (once vs every year)
 *   amountCents signed                        (positive = money in)
 *
 * That decomposition is right for the projection engine and wrong for a person.
 * Nobody thinks "this is a cash flow, and it is negative"; they think "college
 * is a cost I'll pay for four years". The form used to ask both questions
 * directly, as a frequency radio plus a +/- sign toggle, which is the jargon
 * this work package exists to remove.
 *
 * The four combinations are enumerated here as four plain-language choices, so
 * the user answers ONE question they understand and the two model fields are
 * derived. Nothing is lost: the mapping is a bijection, so every event the old
 * form could express is still expressible, and every stored event maps back to
 * exactly one choice for editing.
 *
 * ONE SOURCE OF TRUTH, on purpose. The form, the list rows and the list
 * grouping all read from here. When these strings lived in three components the
 * user could pick "a cost I'll pay every year" and then see it filed under an
 * accounting-flavoured group heading with a frequency badge to match, having
 * learned nothing.
 */
import type { EventType } from '@/types/retirement';

export type EventKind =
  | 'receive_once'
  | 'receive_yearly'
  | 'pay_once'
  | 'pay_yearly';

export interface EventKindOption {
  kind: EventKind;
  /** Radio label. */
  label: string;
  /** Examples shown under the label. These are the discoverability fix. */
  description: string;
  /** Derived model field. */
  eventType: EventType;
  /** Derived sign of amountCents. */
  isIncome: boolean;
}

/**
 * Order matters and is deliberate: the two "receive" options first, then the
 * two "pay" options, each as once-then-every-year. That keeps the underlying
 * 2x2 legible on the page without naming it, and it is also the DOM order, so
 * keyboard order matches reading order.
 */
export const EVENT_KIND_OPTIONS: readonly EventKindOption[] = [
  {
    kind: 'receive_once',
    label: "Money I'll receive once",
    description: 'An inheritance, selling a second home, a work bonus',
    eventType: 'lump_sum',
    isIncome: true,
  },
  {
    kind: 'receive_yearly',
    label: "Money I'll receive every year",
    description: 'Renting out a property, a payout that runs for a few years',
    eventType: 'cash_flow',
    isIncome: true,
  },
  {
    kind: 'pay_once',
    label: "A cost I'll pay once",
    description: 'A second home, a wedding, a new roof',
    eventType: 'lump_sum',
    isIncome: false,
  },
  {
    kind: 'pay_yearly',
    label: "A cost I'll pay every year",
    description: 'College tuition, supporting a parent',
    eventType: 'cash_flow',
    isIncome: false,
  },
] as const;

/**
 * Income sources that already have their OWN fields in the Assumptions region
 * (`socialSecurityAnnualCents`, `pensionAnnualCents`,
 * `otherRetirementIncomeCents` - types/retirement.ts:24-31).
 *
 * They must never appear as examples here. `buildProjectionGrid` adds the
 * assumption income AND the events for each year
 * (retirementCalculations.ts:162-175), so a user who enters Social Security in
 * both places has it counted TWICE and sees a projection that overstates their
 * retirement income - silently, and in the direction that flatters them.
 *
 * Exported so the tests can assert no example ever names one again.
 */
export const INCOME_ALREADY_IN_ASSUMPTIONS = [
  'social security',
  'pension',
] as const;

/**
 * Shown under the chooser. The distinction users need is not income-vs-cost, it
 * is "steady retirement income" (Assumptions) versus "a dated change with a
 * beginning and an end" (here).
 */
export const ASSUMPTIONS_INCOME_HINT =
  'Social Security and pensions belong in Assumptions below, not here — ' +
  'entering them in both places would count them twice.';

/** Look up one option. Total over EventKind, so it cannot return undefined. */
export function eventKindOption(kind: EventKind): EventKindOption {
  const option = EVENT_KIND_OPTIONS.find((o) => o.kind === kind);
  // EVENT_KIND_OPTIONS covers every member of the union; this is unreachable
  // unless the union and the table drift apart, which the tests pin.
  if (!option) throw new Error(`Unknown event kind: ${kind}`);
  return option;
}

/**
 * Map a stored event back to the choice that would produce it.
 *
 * Zero counts as money received, matching the existing list grouping
 * (`amountCents >= 0` is income) so a zero-amount event does not change
 * category on edit.
 */
export function eventKindOf(
  eventType: EventType,
  amountCents: number
): EventKind {
  const isIncome = amountCents >= 0;
  if (eventType === 'cash_flow') {
    return isIncome ? 'receive_yearly' : 'pay_yearly';
  }
  return isIncome ? 'receive_once' : 'pay_once';
}

/** Short badge for a list row: "once" or "every year". */
export function frequencyLabel(eventType: EventType): string {
  return eventType === 'cash_flow' ? 'every year' : 'once';
}

/** Heading for the group a row belongs to. */
export const MONEY_IN_HEADING = 'Money coming in';
export const MONEY_OUT_HEADING = 'Money going out';
